import { Component, Inject, Injector, OnDestroy, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { TranslocoCoreModule } from "@i18n";
import { ButtonWrapperComponent } from "@vui";
import { Customer, Customercontrollerv2Api, SubscriptionApi } from "../../api/proxies";
import { IMaskModule } from "angular-imask";
import { createPostalCodeMask, createCreditCardNumberMask, createCreditCardExpiryMask, createPhoneMask, createFiscalIdentifierMask } from "@ui/input-masks";
import { GetExternalPostalCodeResult, PostalCodeServiceServiceProxy } from "@api";
import { UpdatePaymentMethodStore } from "./store/update-payment-method-store";
import { CardBankService, VINDI_API_BASE_URL, VINDI_TOKEN, PJBANK_API_BASE_URL, PJBANK_ID } from "../../card.service";
import { CreditCard } from "angular-cc-library";
import { environment } from "../../../../../apps/vadmin/src/environments/environment";

export interface UpdatePaymentMethodDialogData { }

@Component({
    selector: "update-payment-method-dialog",
    templateUrl: "./update-payment-method-dialog.component.html",
    styleUrls: ["./update-payment-method-dialog.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        TranslocoCoreModule,
        ButtonWrapperComponent,
        IMaskModule,
    ],
    providers: [
        PostalCodeServiceServiceProxy,
        UpdatePaymentMethodStore,
        Customercontrollerv2Api,
        SubscriptionApi,
        CardBankService,
        {
            provide: VINDI_API_BASE_URL,
            useValue: environment.vindi_api_url,
        },
        {
            provide: VINDI_TOKEN,
            useValue: environment.vindi_public_token,
        },
        { provide: PJBANK_API_BASE_URL, useValue: environment.pjbank_api_url },
        { provide: PJBANK_ID, useValue: environment.pjbank_id },
    ],
})
export class UpdatePaymentMethodDialogComponent implements OnInit, OnDestroy {
    protected _unsubscribeAll: Subject<any>;

    paymentForm!: FormGroup;

    postalCodeMask = createPostalCodeMask();
    creditCardNumberMask = createCreditCardNumberMask();
    creditCardExpiryMask = createCreditCardExpiryMask();
    phoneMask = createPhoneMask();
    fiscalIdentifierMask = createFiscalIdentifierMask();
    originalPostalCode: any;
    invalidPostalCode: boolean = false;

    private _postalCodeService = inject(PostalCodeServiceServiceProxy);
    protected store = inject(UpdatePaymentMethodStore);

    constructor(
        public dialogRef: MatDialogRef<UpdatePaymentMethodDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UpdatePaymentMethodDialogData,
        private fb: FormBuilder,
    ) {
        this._unsubscribeAll = new Subject();
        this.dialogRef.addPanelClass("update-payment-method-dialog");
        this.initializeForm();
    }

    ngOnInit() {
        this.store.loadCustomerInfo().then((customerInfo) => {
            if (customerInfo) {
                this.fillFormWithCustomerData(customerInfo);
            }
        });

        const initialPostalCode = this.paymentForm.get("billingCep")?.value;
        this.originalPostalCode = initialPostalCode
            ? initialPostalCode.replace(/[\\_() -]/g, "")
            : null;

        this.paymentForm
            .get("billingCep")
            ?.valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                let regex = new RegExp("[\\_() -]?", "g");
                let postalCode = value ? value.replace(regex, "") : "";

                if (postalCode.length == 8) {
                    this.validateAddressPostalCode(postalCode);
                } else {
                    this.invalidPostalCode = false;
                    this.clearAddressData();
                }
            });
    }

    initializeForm(): void {
        this.paymentForm = this.fb.group({
            cardHolderName: ['', Validators.required],
            cardNumber: ['', Validators.required],
            cardExpiry: ['', Validators.required],
            cardCvc: ['', Validators.required],
            billingName: ['', Validators.required],
            billingCpfCnpj: ['', Validators.required],
            billingEmail: ['', [Validators.required, Validators.email]],
            billingPhone: ['', Validators.required],
            billingCep: ['', Validators.required],
            billingStreet: ['', Validators.required],
            billingNumber: ['', Validators.required],
            billingComplement: [''],
            billingReference: [''],
            billingNeighborhood: ['', Validators.required],
            billingCity: ['', Validators.required],
            billingState: ['', Validators.required]
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    hasPendingChanges(): boolean {
        return false;
    }

    async onSave(): Promise<void> {
        const creditCardControls = ['cardHolderName', 'cardNumber', 'cardExpiry', 'cardCvc'];
        const creditCardValid = creditCardControls.every(controlName => {
            const control = this.paymentForm.get(controlName);
            return control?.valid;
        });

        const billingControls = ['billingName', 'billingCpfCnpj', 'billingEmail', 'billingPhone', 'billingCep', 'billingStreet', 'billingNumber', 'billingNeighborhood', 'billingCity', 'billingState'];
        const billingValid = billingControls.every(controlName => {
            const control = this.paymentForm.get(controlName);
            return control?.valid;
        });

        if (creditCardValid && billingValid) {
            const formValue = this.paymentForm.value;
            
            const expiryParts = formValue.cardExpiry?.split('/') || [];
            const expirationMonth = expiryParts[0]?.trim() || '';
            const expirationYear = expiryParts[1]?.trim() || '';
            const cardNumber = formValue.cardNumber?.replace(/\s/g, '') || '';
            const cardExpiration = `${expirationMonth}/${expirationYear}`;
            
            const fiscalInfo = {
                name: formValue.billingName || undefined,
                commercialIdentifier: formValue.billingCpfCnpj || undefined,
                email: formValue.billingEmail || undefined,
                phoneNumber: formValue.billingPhone || undefined,
                phoneNumberExtension: "55",
                postalCode: formValue.billingCep?.replace(/\D/g, '') || undefined,
                formattedAddress: formValue.billingStreet || undefined,
                number: formValue.billingNumber || undefined,
                complement: formValue.billingComplement || '',
                reference: formValue.billingReference || '',
                neighborhood: formValue.billingNeighborhood || undefined,
                city: formValue.billingCity || undefined,
                state: formValue.billingState || undefined,
                country : "BR"
            };

            const success = await this.store.tokenizeAndUpdatePaymentInfo(
                {
                    holderName: formValue.cardHolderName || '',
                    cardExpiration: cardExpiration,
                    cardNumber: cardNumber,
                    cardCvv: formValue.cardCvc || '',
                    cardFlag: CreditCard.cardType(cardNumber),
                },
                expirationMonth,
                expirationYear,
                fiscalInfo
            );

            if (success) {
                this.dialogRef.close({
                    formData: this.paymentForm.value,
                    success: true
                });
            }
        } else {
            creditCardControls.forEach(key => {
                this.paymentForm.get(key)?.markAsTouched();
            });
            billingControls.forEach(key => {
                this.paymentForm.get(key)?.markAsTouched();
            });
        }
    }

    
    get submitting(): boolean {
        return this.store.loading();
    }


    close(): void {
        this.dialogRef.close();
    }

    fillFormWithCustomerData(customer: Customer): void {
        if (!customer) {
            return;
        }

        if (customer.fiscalIdentifier) {
            this.paymentForm.patchValue({
                billingCpfCnpj: customer.fiscalIdentifier
            });
        }

        if (customer.address) {
            const address = customer.address;
            this.paymentForm.patchValue({
                billingCep: address.postalCode || '',
                billingStreet: address.formattedAddress || '',
                billingNumber: address.number || '',
                billingComplement: address.complement || '',
                billingReference: address.reference || '',
                billingNeighborhood: address.neighborhood || '',
                billingCity: address.city || '',
                billingState: address.state || ''
            });
        }
    }

    clearAddressData() {
        this.paymentForm.patchValue({
            billingStreet: null,
            billingNumber: null,
            billingComplement: null,
            billingReference: null,
            billingNeighborhood: null,
            billingCity: null,
            billingState: null,
        });
    }

    validateAddressPostalCode(postalCode: string): void {
        if (postalCode === this.originalPostalCode) {
            return;
        }
        this._postalCodeService.validatePostalCode(postalCode).subscribe(
            (result: GetExternalPostalCodeResult) => {
                if (!result.code) {
                    this.invalidPostalCode = true;
                    return;
                }
                this.invalidPostalCode = false;
                this.paymentForm.patchValue({
                    billingStreet: result.address,
                    billingNumber: result.number,
                    billingReference: result.reference,
                    billingNeighborhood: result.neighborhood,
                    billingCity: result.city,
                    billingState: result.state,
                });
                this.paymentForm.markAsDirty();
            },
            () => {
                this.invalidPostalCode = true;
            }
        );
    }
}

